/**
 * Risk Scenarios Chat — Demo chat interface for risk analysis
 * Prefix: rsc-* (Risk Scenarios Chat)
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Shield, ChevronDown, ChevronRight } from 'lucide-react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Collapse from '@mui/material/Collapse';
import SendIcon from '@mui/icons-material/Send';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import graphragService from '../graphrag/services/graphragService';

let msgCounter = 0;
const nextMsgId = () => `msg_${++msgCounter}`;

// -------------------------------------------------------------------
// Risk severity palette
// -------------------------------------------------------------------
const SEVERITY_COLORS = {
  critical: { border: '#b85a5a', bg: 'rgba(210, 115, 115, 0.08)', badge: '#b85a5a' },
  high: { border: '#bc7a45', bg: 'rgba(212, 146, 94, 0.08)', badge: '#bc7a45' },
  medium: { border: '#c9a84a', bg: 'rgba(201, 168, 74, 0.08)', badge: '#c9a84a' },
  low: { border: '#4a9066', bg: 'rgba(93, 168, 122, 0.08)', badge: '#4a9066' },
  default: { border: '#e5e7eb', bg: '#f9fafb', badge: '#6b7280' },
};

// -------------------------------------------------------------------
// Custom markdown renderers for risk-specific styling
// -------------------------------------------------------------------
const getTextContent = (children) => {
  if (typeof children === 'string') return children;
  if (Array.isArray(children)) return children.map(getTextContent).join('');
  if (children?.props?.children) return getTextContent(children.props.children);
  return String(children ?? '');
};

const riskMarkdownComponents = {
  strong({ children }) {
    const text = getTextContent(children).toLowerCase().trim();

    const badgeStyle = (color) => ({
      display: 'inline-block',
      px: '6px',
      py: '1px',
      borderRadius: '4px',
      bgcolor: color,
      color: '#fff',
      fontSize: '0.75em',
      fontWeight: 700,
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
    });

    if (text === 'critical')
      return (
        <Box component="span" sx={badgeStyle('#b85a5a')}>
          {children}
        </Box>
      );
    if (text === 'high')
      return (
        <Box component="span" sx={badgeStyle('#bc7a45')}>
          {children}
        </Box>
      );
    if (text === 'medium')
      return (
        <Box component="span" sx={badgeStyle('#c9a84a')}>
          {children}
        </Box>
      );
    if (text === 'low')
      return (
        <Box component="span" sx={badgeStyle('#4a9066')}>
          {children}
        </Box>
      );

    if (text.includes('immediately') || text.includes('immediate remediation'))
      return (
        <Box component="span" sx={{ color: '#b85a5a', fontWeight: 700 }}>
          {children}
        </Box>
      );
    if (text.match(/^review\b/))
      return (
        <Box component="span" sx={{ color: '#c9a84a', fontWeight: 700 }}>
          {children}
        </Box>
      );
    if (text.match(/^revoke\b/) && !text.includes('immediately'))
      return (
        <Box component="span" sx={{ color: '#b85a5a', fontWeight: 700 }}>
          {children}
        </Box>
      );

    return <strong>{children}</strong>;
  },
  h2({ children }) {
    return (
      <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 1.5, mb: 0.5 }}>
        {children}
      </Typography>
    );
  },
  h3({ children }) {
    return (
      <Typography variant="body2" fontWeight={700} sx={{ mt: 1, mb: 0.25 }}>
        {children}
      </Typography>
    );
  },
  hr() {
    return <Divider sx={{ my: 1 }} />;
  },
};

// -------------------------------------------------------------------
// Risk Card Parser — splits markdown into structured sections
// -------------------------------------------------------------------
const parseRiskResponse = (markdown) => {
  if (!markdown || !markdown.includes('### ')) return null;

  const lines = markdown.split('\n');
  const result = { header: null, preContent: [], cards: [], footer: [] };
  let currentCard = null;
  let foundCards = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    if (trimmed.startsWith('## ')) {
      result.header = trimmed;
      continue;
    }
    if (trimmed.startsWith('### ')) {
      if (currentCard) result.cards.push(currentCard);
      foundCards = true;
      currentCard = { header: trimmed, bodyLines: [], severity: null };
      continue;
    }
    if (trimmed === '---' || trimmed === '***' || trimmed === '___') {
      if (currentCard) {
        result.cards.push(currentCard);
        currentCard = null;
      }
      continue;
    }
    if (!trimmed) continue;
    if (currentCard) {
      currentCard.bodyLines.push(line);
      const lower = trimmed.toLowerCase();
      if (lower.includes('risk level') || lower.startsWith('- **risk')) {
        if (lower.includes('**critical**')) currentCard.severity = 'critical';
        else if (lower.includes('**high**')) currentCard.severity = 'high';
        else if (lower.includes('**medium**')) currentCard.severity = 'medium';
        else if (lower.includes('**low**')) currentCard.severity = 'low';
      }
    } else if (foundCards || result.cards.length > 0) {
      result.footer.push(line);
    } else if (result.header) {
      result.preContent.push(line);
    }
  }

  if (currentCard) result.cards.push(currentCard);
  if (result.cards.length === 0) return null;
  return result;
};

const getCardSummary = (card) => {
  for (const line of card.bodyLines) {
    if (line.trim().toLowerCase().startsWith('- **action:')) {
      return line.replace(/^-\s*/, '').trim();
    }
  }
  if (card.bodyLines.length > 0) {
    return card.bodyLines[0].replace(/^-\s*/, '').trim();
  }
  return '';
};

// -------------------------------------------------------------------
// RiskCard — Collapsible card component
// -------------------------------------------------------------------
const RiskCard = ({ card, defaultExpanded = true }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const severity = card.severity || 'default';
  const palette = SEVERITY_COLORS[severity] || SEVERITY_COLORS.default;
  const bodyMarkdown = card.bodyLines.join('\n');
  const summaryMarkdown = getCardSummary(card);

  return (
    <Box
      sx={{
        border: `1px solid ${palette.border}`,
        borderLeft: `4px solid ${palette.border}`,
        borderRadius: 1.5,
        bgcolor: palette.bg,
        overflow: 'hidden',
        mb: 1,
      }}
    >
      {/* Header */}
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          px: 1.5,
          py: 1,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Box sx={{ mt: 0.25, flexShrink: 0 }}>
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </Box>
        <Box sx={{ flex: 1, '& p': { m: 0 }, '& strong': { fontWeight: 700 } }}>
          <Typography variant="body2" fontWeight={600} component="div">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={riskMarkdownComponents}>
              {card.header.replace(/^###\s*/, '')}
            </ReactMarkdown>
          </Typography>
        </Box>
      </Box>

      {/* Collapsed summary */}
      {!expanded && summaryMarkdown && (
        <Box
          sx={{
            px: 2,
            pb: 1,
            '& p': { m: 0 },
            '& strong': { fontWeight: 700 },
          }}
        >
          <Typography variant="caption" color="text.secondary" component="div">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={riskMarkdownComponents}>
              {summaryMarkdown}
            </ReactMarkdown>
          </Typography>
        </Box>
      )}

      {/* Expanded body */}
      <Collapse in={expanded}>
        <Box
          sx={{
            px: 2,
            pb: 1.5,
            '& p': { m: 0, mb: 0.5 },
            '& ul': { pl: 2, m: 0 },
            '& li': { mb: 0.25 },
            '& strong': { fontWeight: 700 },
          }}
        >
          <Typography variant="body2" component="div">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={riskMarkdownComponents}>
              {bodyMarkdown}
            </ReactMarkdown>
          </Typography>
        </Box>
      </Collapse>
    </Box>
  );
};

// -------------------------------------------------------------------
// RiskCardResponse — Structured rendering of parsed risk response
// -------------------------------------------------------------------
const RiskCardResponse = ({ parsed }) => {
  return (
    <Box>
      {parsed.header && (
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={riskMarkdownComponents}>
            {parsed.header.replace(/^##\s*/, '')}
          </ReactMarkdown>
        </Typography>
      )}
      {parsed.preContent.length > 0 && (
        <Box
          sx={{
            mb: 1.5,
            '& p': { m: 0 },
            '& strong': { fontWeight: 700 },
          }}
        >
          <Typography variant="body2" component="div">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={riskMarkdownComponents}>
              {parsed.preContent.join('\n')}
            </ReactMarkdown>
          </Typography>
        </Box>
      )}
      {parsed.cards.map((card, i) => (
        <RiskCard key={i} card={card} defaultExpanded={true} />
      ))}
      {parsed.footer.length > 0 && (
        <Box
          sx={{
            mt: 1.5,
            '& p': { m: 0 },
            '& strong': { fontWeight: 700 },
          }}
        >
          <Typography variant="body2" component="div">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={riskMarkdownComponents}>
              {parsed.footer.join('\n')}
            </ReactMarkdown>
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// -------------------------------------------------------------------
// MessageBubble
// -------------------------------------------------------------------
const MessageBubble = React.memo(({ message, onChipClick }) => {
  const isUser = message.role === 'user';
  const parsed = !isUser ? parseRiskResponse(message.content) : null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isUser ? 'row-reverse' : 'row',
        gap: 1,
        mb: 1.5,
        alignItems: 'flex-start',
      }}
    >
      {/* AI avatar */}
      {!isUser && (
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            bgcolor: 'warning.main',
            color: 'warning.contrastText',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            mt: 0.5,
          }}
        >
          <Shield size={14} />
        </Box>
      )}

      {/* Bubble */}
      <Box sx={{ maxWidth: '85%' }}>
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderRadius: isUser ? '16px 4px 16px 16px' : '4px 16px 16px 16px',
            bgcolor: isUser ? 'primary.main' : 'background.paper',
            color: isUser ? 'primary.contrastText' : 'text.primary',
            border: isUser ? 'none' : '1px solid',
            borderColor: 'divider',
            '& p': { m: 0 },
            '& p + p': { mt: 0.5 },
            '& ul, & ol': { pl: 2, m: 0 },
            '& li': { mb: 0.25 },
            '& strong': { fontWeight: 700 },
          }}
        >
          {isUser ? (
            <Typography variant="body2">{message.content}</Typography>
          ) : parsed ? (
            <RiskCardResponse parsed={parsed} />
          ) : (
            <Typography variant="body2" component="div">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={riskMarkdownComponents}>
                {message.content}
              </ReactMarkdown>
            </Typography>
          )}
        </Box>

        {/* Suggestions */}
        {message.suggestions?.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
            {message.suggestions.map((chip, i) => (
              <Chip
                key={i}
                label={chip}
                size="small"
                variant="outlined"
                onClick={() => onChipClick(chip)}
                sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
              />
            ))}
          </Box>
        )}

        {/* Timestamp */}
        {message.timestamp && (
          <Typography
            variant="caption"
            color="text.disabled"
            display="block"
            sx={{ mt: 0.5, textAlign: isUser ? 'right' : 'left' }}
          >
            {new Date(message.timestamp).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </Typography>
        )}
      </Box>
    </Box>
  );
});

// -------------------------------------------------------------------
// RiskScenariosPage
// -------------------------------------------------------------------
const RiskScenariosPage = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  const tenantName = user?.['https://trustx.cloud/claims/company'];

  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState(null);
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, chatLoading]);

  const addMessage = useCallback((msg) => {
    setMessages((prev) => [...prev, { id: nextMsgId(), timestamp: new Date(), ...msg }]);
  }, []);

  // Initialize
  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      if (!tenantName) {
        setError('Unable to determine organization. Please ensure your account is configured.');
        return;
      }
      try {
        const token = await getAccessTokenSilently();
        graphragService.setAuthToken(token);
        if (cancelled) return;
        setIsInitialized(true);

        let starters = [];
        try {
          const sugResult = await graphragService.getDemoRiskChatSuggestions();
          if (sugResult.success && sugResult.data?.suggestions) {
            starters = sugResult.data.suggestions;
          }
        } catch {
          // fall through — use hardcoded defaults
        }

        if (starters.length === 0) {
          starters = [
            'What are my most critical access risks right now?',
            'Show me all expired contractors who still have active access',
            'Who has more access than their peers in the same role?',
            'Show me all segregation of duties violations that could affect my SOC 2 or SOX audit',
          ];
        }

        if (cancelled) return;

        setMessages([
          {
            id: nextMsgId(),
            role: 'assistant',
            content:
              'Welcome to **Risk Scenario Analysis**. I have access to the complete risk report for TrustX Inc, including expired contractors, dormant accounts, privilege escalations, and SOX compliance violations.\n\nWhat would you like to investigate?',
            timestamp: new Date(),
            suggestions: starters.slice(0, 6),
          },
        ]);
      } catch (err) {
        if (!cancelled) setError('Failed to initialize. Please refresh the page.');
      }
    };

    init();
    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently, tenantName]);

  const getConversationHistory = useCallback(() => {
    return messages
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .slice(-10)
      .map((m) => ({ role: m.role, content: m.content }));
  }, [messages]);

  const processMessage = useCallback(
    async (message) => {
      addMessage({ role: 'user', content: message });
      setChatLoading(true);

      try {
        const history = getConversationHistory();
        const response = await graphragService.demoRiskChat(message, history);

        if (response.success && response.data) {
          addMessage({
            role: 'assistant',
            content: response.data.response || 'I could not generate a response.',
            suggestions: response.data.suggestions || [],
          });
        } else {
          addMessage({
            role: 'assistant',
            content: 'I encountered an error analyzing the risk data. Please try again.',
          });
        }
      } catch {
        addMessage({
          role: 'assistant',
          content: 'I encountered an error processing your question. Please try again.',
        });
      } finally {
        setChatLoading(false);
      }
    },
    [addMessage, getConversationHistory],
  );

  const handleSend = useCallback(() => {
    if (!chatInput.trim() || chatLoading) return;
    const message = chatInput.trim();
    setChatInput('');
    processMessage(message);
  }, [chatInput, chatLoading, processMessage]);

  const handleChipClick = useCallback(
    (chip) => {
      if (chatLoading) return;
      processMessage(chip);
    },
    [chatLoading, processMessage],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          flexShrink: 0,
        }}
      >
        <WarningAmberIcon fontSize="small" color="warning" />
        <Typography variant="subtitle1" fontWeight={700}>
          Risk Scenario Analysis
        </Typography>
      </Box>

      {/* Error banner */}
      {error && (
        <Alert severity="error" sx={{ borderRadius: 0 }}>
          {error}
        </Alert>
      )}

      {/* Chat container */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Messages */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: 2,
            py: 2,
          }}
        >
          {messages.map((msg) => (
            <MessageBubble key={msg.id} message={msg} onChipClick={handleChipClick} />
          ))}

          {chatLoading && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1.5 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  bgcolor: 'warning.main',
                  color: 'warning.contrastText',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  mt: 0.5,
                }}
              >
                <Shield size={14} />
              </Box>
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  borderRadius: '4px 16px 16px 16px',
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {[0, 1, 2].map((i) => (
                    <Box
                      key={i}
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: 'warning.main',
                        animation: 'bounce 1.4s infinite',
                        animationDelay: `${i * 0.2}s`,
                        '@keyframes bounce': {
                          '0%, 60%, 100%': { transform: 'translateY(0)' },
                          '30%': { transform: 'translateY(-6px)' },
                        },
                      }}
                    />
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Analyzing risk data...
                </Typography>
              </Box>
            </Box>
          )}

          <div ref={messagesEndRef} />
        </Box>

        {/* Input area */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Ask about risk scenarios, people, SOX violations..."
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              disabled={chatLoading || !isInitialized}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
            />
            <IconButton
              color="warning"
              onClick={handleSend}
              disabled={chatLoading || !chatInput.trim()}
              sx={{
                bgcolor: 'warning.main',
                color: 'warning.contrastText',
                '&:hover': { bgcolor: 'warning.dark' },
                '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
              }}
            >
              <SendIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default RiskScenariosPage;
